import React, { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import OTPVerification from '../OTPVerification/OTPVerification';
import './LoginSignup.css';

const LoginSignup = ({ setIsLoggedIn, setCurrentPage, setUser, onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showOTP, setShowOTP] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const services = [
    {
      title: "IP Camera Systems",
      description: "High-definition security cameras with night vision and motion detection",
      icon: "📹"
    },
    {
      title: "NVR Solutions", 
      description: "Network Video Recorders for centralized surveillance management",
      icon: "💾"
    },
    {
      title: "VoIP Phone Systems",
      description: "Professional communication systems for business connectivity",
      icon: "📞"
    }
  ];

  const products = [
    {
      title: "Home Security",
      description: "Complete residential surveillance and communication packages",
      icon: "🏠"
    },
    {
      title: "Commercial Solutions",
      description: "Enterprise-grade security systems for businesses and offices", 
      icon: "🏢"
    },
    {
      title: "Remote Monitoring",
      description: "24/7 cloud-based monitoring and mobile app access",
      icon: "📱"
    }
  ];

  const allItems = [...services, ...products];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % allItems.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [allItems.length]);

  const validateForm = () => {
    const newErrors = {};

    if (!isLogin && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isLogin && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 handleSubmit triggered");

    if (!validateForm()) {
      console.log("❌ Frontend validation failed");
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      let response;

      if (isLogin) {
        // LOGIN FLOW (No changes)
        const payload = {
          email: formData.email,
          password: formData.password
        };

        console.log("📤 Login payload being sent:", payload);
        response = await ApiService.login(payload);
        console.log("📥 Login API response:", response);

        if (response.success && response.user) {
          const userData = response.user;
          const userRole = userData.role;
          
          console.log("✅ Login successful!", {
            email: userData.email,
            role: userRole,
            isAdmin: userRole === 'admin'
          });

          setUser(userData);

          if (onLogin) {
            onLogin(userData);
          } else {
            setIsLoggedIn(true);
            
            if (userRole === 'admin') {
              setCurrentPage('admin-dashboard');
              console.log("🔑 Admin user detected, redirecting to admin dashboard");
            } else {
              setCurrentPage('home');
              console.log("👤 Regular user detected, redirecting to home page");
            }
          }
        }

      } else {
        // REGISTRATION FLOW (NEW - Shows OTP screen)
        const payload = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword
        };

        console.log("📤 Register payload being sent:", payload);
        response = await ApiService.register(payload);
        console.log("📥 Register API response:", response);

        // NEW: Check if OTP verification is required
        if (response.success && response.requiresOTP) {
          console.log("📧 OTP required, showing verification screen");
          setRegistrationEmail(response.email || formData.email);
          setShowOTP(true);
          return; // Stop here, don't log in yet
        }
      }

    } catch (error) {
      console.error('❌ Authentication error caught in catch():', error);

      let errorMessage = 'An error occurred. Please try again.';

      if (error.message.includes('Invalid credentials') || 
          error.message.includes('Invalid email or password') ||
          error.message.includes('Incorrect password')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.message.includes('verify your email')) {
        errorMessage = 'Please verify your email before logging in. Check your inbox for the verification code.';
      } else if (error.message.includes('User not found') || 
                 error.message.includes('No account found')) {
        errorMessage = 'No account found with this email address. Please check your email or create a new account.';
      } else if (error.message.includes('already registered') || 
                 error.message.includes('already exists')) {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.message.includes('Account is deactivated') ||
                 error.message.includes('suspended') ||
                 error.message.includes('blocked')) {
        errorMessage = 'Your account has been deactivated. Please contact support for assistance.';
      } else if (error.message.includes('Admin access required')) {
        errorMessage = 'Access denied. This account does not have the required permissions.';
      } else if (error.message.includes('Network') || 
                 error.message.includes('connection') ||
                 error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (error.message.includes('Validation failed')) {
        errorMessage = 'Please check that all fields are filled correctly and passwords match.';
      } else if (error.message.includes('Server error') ||
                 error.message.includes('500')) {
        errorMessage = 'Server error occurred. Please try again in a moment.';
      } else if (error.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (error.status === 403) {
        errorMessage = 'Access denied. Please check your account permissions.';
      } else if (error.status === 404) {
        errorMessage = 'Account not found. Please check your email or create a new account.';
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
      console.log("✅ handleSubmit finished");
    }
  };

  // NEW: Handle successful OTP verification
  const handleOTPVerified = (response) => {
    console.log("✅ OTP verified successfully!", response);

    if (response.success && response.user) {
      const userData = response.user;
      const userRole = userData.role;
      
      setUser(userData);

      if (onLogin) {
        onLogin(userData);
      } else {
        setIsLoggedIn(true);
        
        if (userRole === 'admin') {
          setCurrentPage('admin-dashboard');
        } else {
          setCurrentPage('home');
        }
      }
    }
  };

  // NEW: Handle back from OTP screen
  const handleBackFromOTP = () => {
    setShowOTP(false);
    setRegistrationEmail('');
  };

  const switchToSignup = () => {
    setIsLogin(false);
    setShowOTP(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
  };

  const switchToLogin = () => {
    setIsLogin(true);
    setShowOTP(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
  };

  // NEW: Show OTP screen if needed
  if (showOTP) {
    return (
      <OTPVerification
        email={registrationEmail}
        onVerified={handleOTPVerified}
        onBack={handleBackFromOTP}
      />
    );
  }

  return (
    <div className="login-signup-container">
      {/* Floating Background Elements */}
      <div className="floating-element floating-element-1"></div>
      <div className="floating-element floating-element-2"></div>
      <div className="floating-element floating-element-3"></div>

      <div className="main-wrapper">
        {/* Information Panel */}
        <div className="info-panel">
          <div className="info-panel-content">
            {/* Company Logo and Header */}
            <div className="company-header">
              {/* PROMINENT LOGO SECTION */}
              <div className="logo-section">
                <img 
                  src="/images/logo.jpg" 
                  alt="Transcontinental Network Logo" 
                  className="logo-image"
                />
              </div>
              
              <div className="company-info">
                <h1 className="company-name">
                  TRANSCONTINENTAL NETWORK
                </h1>
              </div>
            </div>

            {/* Rotating Content */}
            <div className="rotating-content">
              <div 
                className="content-slider" 
                style={{ transform: `translateY(-${currentSlide * 128}px)` }}
              >
                {allItems.map((item, index) => (
                  <div key={index} className="slide-item">
                    <div className="slide-icon">{item.icon}</div>
                    <div className="slide-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-number">500+</div>
                <div className="feature-label">Systems Installed</div>
              </div>
              <div className="feature-item">
                <div className="feature-number">24/7</div>
                <div className="feature-label">Technical Support</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Panel */}
        <div className={`form-panel ${isLogin ? 'login-mode' : 'signup-mode'}`}>
          <div className="form-header">
            <h2 className="form-title">
              {isLogin ? 'Welcome Back' : 'Join Our Network'}
            </h2>
            <p className="form-subtitle">
              {isLogin ? 'Sign in to access your dashboard' : 'Create your account to get started'}
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {!isLogin && (
              <div className={`input-group name-input ${!isLogin ? 'show' : ''}`}>
                <label className="input-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
            )}

            <div className="input-group">
              <label className="input-label">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your email address"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            {!isLogin && (
              <div className={`input-group confirm-password ${!isLogin ? 'show' : ''}`}>
                <label className="input-label">Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`submit-btn ${isLoading ? 'loading' : ''}`}
            >
              {isLoading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>

            {errors.submit && <span className="error-message submit-error">{errors.submit}</span>}
          </form>

          <div className="form-footer">
            <div className="switch-mode">
              {isLogin ? (
                <p>
                  Don't have an account?{' '}
                  <button className="switch-btn" onClick={switchToSignup}>
                    Sign Up
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button className="switch-btn" onClick={switchToLogin}>
                    Sign In
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;